import axios from "axios";
import { useEffect, useState } from "react";
import { Calendar2Week } from "react-bootstrap-icons";

import { ApiConnectionString } from "../../../constants/ApiConntection";
import LoadingSpinner from "../../../components/LoadingSpinner";

import SettingsCard from "./SettingsCard";
import SectionMessage from "./SectionMessage";

import {
  createEmptyMessage,
  getErrorMessage,
} from "../settingsUtils";

const emptyHolidayForm = {
  startDate: "",
  endDate: "",
  reason: "",
};

export default function HolidaySettings({ token }) {
  const [holidays, setHolidays] = useState([]);

  const [form, setForm] =
    useState(emptyHolidayForm);

  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);

  const [deletingHolidayId, setDeletingHolidayId] =
    useState(null);

  const [message, setMessage] =
    useState(createEmptyMessage);

  const getHolidays = async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${ApiConnectionString}/api/holidays`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setHolidays(response.data || []);
    } catch (error) {
      console.error(error);

      setMessage({
        error: getErrorMessage(
          error,
          "Failed to load holidays."
        ),
        success: "",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getHolidays();
  }, [token]);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setMessage(createEmptyMessage());
  };

  const createHoliday = async () => {
    if (!form.startDate) {
      setMessage({
        error: "Please choose a start date.",
        success: "",
      });

      return;
    }

    if (!form.endDate) {
      setMessage({
        error: "Please choose an end date.",
        success: "",
      });

      return;
    }

    if (form.endDate < form.startDate) {
      setMessage({
        error:
          "The holiday end date cannot be before the start date.",
        success: "",
      });

      return;
    }

    try {
      setIsAdding(true);
      setMessage(createEmptyMessage());

      await axios.post(
        `${ApiConnectionString}/api/holiday`,
        {
          startDate: form.startDate,
          endDate: form.endDate,
          reason: form.reason.trim(),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setForm(emptyHolidayForm);

      await getHolidays();

      setMessage({
        error: "",
        success: "Holiday added successfully.",
      });
    } catch (error) {
      console.error(error);

      setMessage({
        error: getErrorMessage(
          error,
          "Failed to add holiday."
        ),
        success: "",
      });
    } finally {
      setIsAdding(false);
    }
  };

  const deleteHoliday = async (id) => {
    try {
      setDeletingHolidayId(id);
      setMessage(createEmptyMessage());

      await axios.delete(
        `${ApiConnectionString}/api/holiday/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setHolidays((current) =>
        current.filter((holiday) => holiday.id !== id)
      );

      setMessage({
        error: "",
        success: "Holiday deleted successfully.",
      });
    } catch (error) {
      console.error(error);

      setMessage({
        error: getErrorMessage(
          error,
          "Failed to delete holiday."
        ),
        success: "",
      });
    } finally {
      setDeletingHolidayId(null);
    }
  };

  return (
    <SettingsCard
      icon={<Calendar2Week />}
      title="Holidays"
      description="Block dates when clients should not be able to book."
    >
      <div className="settings-form">
        {isLoading ? (
          <div className="availability-settings-loading">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <SectionMessage message={message} />

            <div className="holiday-row">
              <div>
                <label htmlFor="holiday-start-date">
                  Start date
                </label>

                <input
                  id="holiday-start-date"
                  type="date"
                  value={form.startDate}
                  onChange={(event) =>
                    updateField(
                      "startDate",
                      event.target.value
                    )
                  }
                />
              </div>

              <div>
                <label htmlFor="holiday-end-date">
                  End date
                </label>

                <input
                  id="holiday-end-date"
                  type="date"
                  min={form.startDate || undefined}
                  value={form.endDate}
                  onChange={(event) =>
                    updateField(
                      "endDate",
                      event.target.value
                    )
                  }
                />
              </div>

              <div>
                <label htmlFor="holiday-reason">
                  Reason
                </label>

                <input
                  id="holiday-reason"
                  type="text"
                  placeholder="Optional"
                  value={form.reason}
                  onChange={(event) =>
                    updateField(
                      "reason",
                      event.target.value
                    )
                  }
                />
              </div>
            </div>

            <div className="settings-actions">
              <button
                type="button"
                onClick={createHoliday}
                disabled={isAdding}
              >
                {isAdding
                  ? "Adding..."
                  : "Add holiday"}
              </button>
            </div>

            {holidays.length > 0 ? (
              <div className="holiday-list">
                {holidays.map((holiday) => (
                  <div
                    className="holiday-item"
                    key={holiday.id}
                  >
                    <div>
                      <strong>
                        {holiday.startDate} to{" "}
                        {holiday.endDate}
                      </strong>

                      <span>
                        {holiday.reason ||
                          "No reason provided"}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="availability-delete-btn"
                      onClick={() =>
                        deleteHoliday(holiday.id)
                      }
                      disabled={
                        deletingHolidayId === holiday.id
                      }
                    >
                      {deletingHolidayId === holiday.id
                        ? "Deleting..."
                        : "Delete"}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="availability-empty-text">
                No holidays have been added.
              </p>
            )}
          </>
        )}
      </div>
    </SettingsCard>
  );
}